import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronLeft,
    faGripVertical,
    faMagnifyingGlass,
    faPencil,
    faPlus,
    faXmark,
    faTrash,
    faShareNodes,
    faStar,
    faSpinner,
    faTriangleExclamation,
    faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import {
    fetchCatalogue,
    fetchReportDetail,
    previewRecipe,
    createReport,
    updateReport,
    deleteReport,
    setReportShared,
    setReportLocked,
} from '../../services/reportingService';
import {
    emptyRecipe,
    addColumn,
    removeColumn,
    moveColumn,
    renameColumn,
    toServer,
    isDirty,
    fromDetail,
    opTakesNoValue,
    opTakesPair,
    opTakesList,
} from './recipe';
import ResultTable from './ResultTable';

// How long after the last change the live preview is asked for. Long enough
// that typing a filter value does not fire a request per keystroke.
const PREVIEW_DELAY_MS = 600;
const PREVIEW_ROWS = 20;
const MAX_SORT_RULES = 3;

/** One data point in the left rail: drag it onto the strip, or click to add it. */
const PaletteChip = ({ point, inReport, onAdd }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `palette:${point.key}`,
        data: { from: 'palette', key: point.key },
    });
    return (
        <button
            ref={setNodeRef}
            type="button"
            className={`rc-palette-chip ${inReport ? 'rc-palette-chip-used' : ''}`}
            style={{ opacity: isDragging ? 0.5 : 1 }}
            title={point.help || point.label}
            aria-label={inReport ? `${point.label} is already in the report` : `Add ${point.label} to the report`}
            {...attributes}
            {...listeners}
            onClick={onAdd}
            onKeyDown={(e) => {
                // Enter/Space adds the point. Handled here rather than by the
                // drag library, so a keyboard user never has to drag anything.
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAdd();
                }
            }}
        >
            <FontAwesomeIcon icon={faGripVertical} className="rc-chip-grip" />
            <span className="rc-chip-text">{point.label}</span>
        </button>
    );
};

/** One column in the strip: drag the grip to reorder, pencil to rename, × to remove. */
const ColumnChip = ({ column, label, editing, onStartEdit, onCommitEdit, onCancelEdit, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: column.key,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    const shown = column.header || label;

    return (
        <div ref={setNodeRef} style={style} className="rc-column-chip">
            <button
                type="button"
                className="rc-chip-handle"
                aria-label={`Reorder ${shown}`}
                {...attributes}
                {...listeners}
            >
                <FontAwesomeIcon icon={faGripVertical} />
            </button>
            {editing ? (
                <input
                    className="rc-chip-rename"
                    autoFocus
                    defaultValue={column.header}
                    placeholder={label}
                    aria-label={`Heading for ${label}`}
                    onBlur={(e) => onCommitEdit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onCommitEdit(e.target.value);
                        }
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            onCancelEdit();
                        }
                    }}
                />
            ) : (
                <span className="rc-chip-text">{shown}</span>
            )}
            <button
                type="button"
                className="rc-chip-icon-btn"
                onClick={onStartEdit}
                aria-label={`Rename the ${shown} heading`}
            >
                <FontAwesomeIcon icon={faPencil} />
            </button>
            <button
                type="button"
                className="rc-chip-icon-btn"
                onClick={onRemove}
                aria-label={`Remove ${shown} from the report`}
            >
                <FontAwesomeIcon icon={faXmark} />
            </button>
        </div>
    );
};

/** The dashed target at the end of the strip. */
const ColumnDropTarget = () => {
    const { setNodeRef, isOver } = useDroppable({ id: 'rc-columns-drop' });
    return (
        <div ref={setNodeRef} className={`rc-drop-target ${isOver ? 'rc-drop-target-over' : ''}`}>
            Drop a data point here
        </div>
    );
};

/** The value box (or boxes, or none) a comparison needs, for this kind of data. */
const FilterValue = ({ point, row, onChange }) => {
    const kind = point?.kind || 'text';
    const inputType = kind === 'date' ? 'date'
        : (kind === 'text' ? 'text' : 'number');

    if (opTakesNoValue(row.op)) return <span className="rc-filter-novalue">nothing to type</span>;

    if (opTakesPair(row.op)) {
        const pair = Array.isArray(row.value) ? row.value : ['', ''];
        return (
            <div className="rc-filter-pair">
                <input
                    type={inputType}
                    className="rc-input"
                    value={pair[0] ?? ''}
                    aria-label={`${point?.label || 'Value'} from`}
                    onChange={(e) => onChange([e.target.value, pair[1] ?? ''])}
                />
                <span className="rc-filter-and">and</span>
                <input
                    type={inputType}
                    className="rc-input"
                    value={pair[1] ?? ''}
                    aria-label={`${point?.label || 'Value'} to`}
                    onChange={(e) => onChange([pair[0] ?? '', e.target.value])}
                />
            </div>
        );
    }

    if (opTakesList(row.op)) {
        const asText = Array.isArray(row.value) ? row.value.join(', ') : String(row.value ?? '');
        return (
            <input
                type="text"
                className="rc-input"
                value={asText}
                placeholder="Separate each one with a comma"
                aria-label={`${point?.label || 'Value'} — one of these`}
                onChange={(e) => onChange(e.target.value.split(',').map((v) => v.trim()))}
            />
        );
    }

    return (
        <input
            type={inputType}
            className="rc-input"
            value={Array.isArray(row.value) ? '' : (row.value ?? '')}
            aria-label={`${point?.label || 'Value'}`}
            onChange={(e) => onChange(e.target.value)}
        />
    );
};

/**
 * Build a report by choosing what one row stands for and dragging in the pieces.
 *
 * Nothing here decides what a report may show. The catalogue says which subjects
 * this person can use and why not when they cannot; the live preview runs the
 * draft through the same engine, with the same access checks, as a saved report.
 * A refusal is shown as the sentence the server wrote — never rephrased here.
 */
const ReportBuilder = ({ report, seed, canLock = false, onBack, onSaved }) => {
    const { showSuccess } = useFlashMessage();
    const [subjects, setSubjects] = useState([]);
    const [recipe, setRecipe] = useState(emptyRecipe(''));
    const [baseline, setBaseline] = useState(emptyRecipe(''));
    const [saved, setSaved] = useState(report || null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [formError, setFormError] = useState('');
    const [busy, setBusy] = useState('');
    const [editingKey, setEditingKey] = useState('');
    const [search, setSearch] = useState('');

    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState('');
    // Only the newest preview answer may land — an older, slower one must not
    // overwrite the table with a result for columns that are no longer chosen.
    const previewSeq = useRef(0);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        let alive = true;
        const start = async () => {
            setLoading(true);
            setLoadError('');
            try {
                const catalogue = await fetchCatalogue();
                if (!alive) return;
                const list = catalogue.subjects || [];
                setSubjects(list);
                if (report) {
                    const detail = await fetchReportDetail(report.id);
                    if (!alive) return;
                    const loaded = fromDetail(detail);
                    setRecipe(loaded);
                    setBaseline(loaded);
                } else if (seed?.recipe) {
                    // An Ask AI answer opened for editing: never saved, so the
                    // baseline is blank and it reads as unsaved work.
                    const loaded = { ...fromDetail(seed.recipe), name: seed.name || '' };
                    setRecipe(loaded);
                    setBaseline(emptyRecipe(loaded.subject));
                } else {
                    const usable = list.find((s) => !s.locked_reason);
                    const blank = emptyRecipe(usable ? usable.key : '');
                    setRecipe(blank);
                    setBaseline(blank);
                }
            } catch (err) {
                if (alive) setLoadError(extractApiError(err, 'Could not open the report builder.'));
            } finally {
                if (alive) setLoading(false);
            }
        };
        start();
        return () => { alive = false; };
    }, [report, seed]);

    const subject = useMemo(
        () => subjects.find((s) => s.key === recipe.subject) || null,
        [subjects, recipe.subject],
    );

    const pointsByKey = useMemo(() => {
        const map = {};
        (subject?.groups || []).forEach((group) => {
            (group.data_points || []).forEach((point) => { map[point.key] = point; });
        });
        return map;
    }, [subject]);

    const labelFor = useCallback((key) => pointsByKey[key]?.label || key, [pointsByKey]);

    const usableSubjects = useMemo(() => subjects.filter((s) => !s.locked_reason), [subjects]);

    // The body the preview would be asked for. Kept as text so the preview does
    // not re-run while someone is only typing the report's name.
    const previewBody = useMemo(() => JSON.stringify(toServer(recipe)), [recipe]);

    useEffect(() => {
        const body = JSON.parse(previewBody);
        if (loading || !body.subject || body.columns.length === 0) {
            setPreview(null);
            setPreviewError('');
            return undefined;
        }
        const seq = previewSeq.current + 1;
        previewSeq.current = seq;
        const timer = setTimeout(async () => {
            setPreviewing(true);
            try {
                const data = await previewRecipe(body);
                if (previewSeq.current !== seq) return;
                setPreview(data);
                setPreviewError('');
            } catch (err) {
                if (previewSeq.current !== seq) return;
                setPreview(null);
                setPreviewError(extractApiError(err, 'This report cannot be run as it stands.'));
            } finally {
                if (previewSeq.current === seq) setPreviewing(false);
            }
        }, PREVIEW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [previewBody, loading]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;
        if (active.data.current?.from === 'palette') {
            setRecipe((current) => addColumn(current, active.data.current.key));
            return;
        }
        if (active.id === over.id) return;
        setRecipe((current) => {
            const from = current.columns.findIndex((c) => c.key === active.id);
            const to = current.columns.findIndex((c) => c.key === over.id);
            return to === -1 ? current : moveColumn(current, from, to);
        });
    };

    const changeSubject = (key) => {
        if (key === recipe.subject) return;
        if (recipe.columns.length > 0
            && !window.confirm('Changing what one row stands for clears the columns, filters and sort. Continue?')) {
            return;
        }
        setRecipe((current) => ({ ...emptyRecipe(key), name: current.name, description: current.description }));
    };

    const setFilter = (index, patch) => setRecipe((current) => ({
        ...current,
        filters: current.filters.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

    const setSort = (index, patch) => setRecipe((current) => ({
        ...current,
        sort: current.sort.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    }));

    const toggleTotal = (key) => setRecipe((current) => ({
        ...current,
        totals: current.totals.includes(key)
            ? current.totals.filter((t) => t !== key)
            : [...current.totals, key],
    }));

    const dirty = isDirty(recipe, baseline);

    const handleBack = () => {
        if (dirty && !window.confirm('Leave without saving? The changes you made will be lost.')) return;
        onBack();
    };

    const handleSave = async () => {
        setBusy('save');
        setFormError('');
        try {
            const body = {
                ...toServer(recipe),
                name: recipe.name,
                description: recipe.description,
            };
            const card = saved
                ? await updateReport(saved.id, body)
                : await createReport({ ...body, origin: 'builder' });
            setSaved(card);
            setBaseline(recipe);
            showSuccess('Saved.');
            onSaved();
        } catch (err) {
            setFormError(extractApiError(err, 'Could not save this report.'));
        } finally {
            setBusy('');
        }
    };

    const runOnSaved = async (what, action) => {
        setBusy(what);
        setFormError('');
        try {
            const card = await action();
            if (card) setSaved(card);
            onSaved();
            return card;
        } catch (err) {
            setFormError(extractApiError(err, 'Could not change this report.'));
            return null;
        } finally {
            setBusy('');
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${saved.name}"? This cannot be undone.`)) return;
        setBusy('delete');
        setFormError('');
        try {
            await deleteReport(saved.id);
            onSaved();
            onBack();
        } catch (err) {
            setFormError(extractApiError(err, 'Could not delete this report.'));
            setBusy('');
        }
    };

    const crumbName = saved?.name || recipe.name || 'New report';

    return (
        <div className="rc-builder">
            <div className="rc-breadcrumb">
                <button type="button" className="rc-crumb-btn" onClick={handleBack}>
                    <FontAwesomeIcon icon={faChevronLeft} /> My reports
                </button>
                <span className="rc-crumb-sep">›</span>
                <span className="rc-crumb-current">{crumbName}</span>
                {dirty && <span className="rc-unsaved">Unsaved changes</span>}
            </div>

            {loading ? (
                <div className="rc-panel rc-panel-message">Loading the report builder…</div>
            ) : loadError ? (
                <div className="rc-panel">
                    <div className="rc-error">
                        <FontAwesomeIcon icon={faTriangleExclamation} /> {loadError}
                    </div>
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <div className="rc-builder-body">
                        {/* LEFT RAIL — what can go in the report */}
                        <aside className="rc-panel rc-rail">
                            <div className="rc-search rc-rail-search">
                                <FontAwesomeIcon icon={faMagnifyingGlass} className="rc-search-icon" />
                                <input
                                    type="text"
                                    aria-label="Search data points"
                                    placeholder="Search data points…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            {(subject?.groups || []).map((group) => {
                                const query = search.trim().toLowerCase();
                                const points = (group.data_points || []).filter((point) => (
                                    !query || point.label.toLowerCase().includes(query)
                                ));
                                if (points.length === 0) return null;
                                return (
                                    <div key={group.name} className="rc-rail-group">
                                        <div className="rc-rail-group-title">{group.name}</div>
                                        <div className="rc-rail-chips">
                                            {points.map((point) => (
                                                <PaletteChip
                                                    key={point.key}
                                                    point={point}
                                                    inReport={recipe.columns.some((c) => c.key === point.key)}
                                                    onAdd={() => setRecipe((current) => addColumn(current, point.key))}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </aside>

                        {/* MAIN — the report itself */}
                        <div className="rc-builder-main">
                            <div className="rc-panel">
                                <div className="rc-builder-top">
                                    <div className="rc-field rc-grow">
                                        <label className="rc-field-label" htmlFor="rc-report-name">Name</label>
                                        <input
                                            id="rc-report-name"
                                            type="text"
                                            className="rc-input"
                                            placeholder="What is this report called?"
                                            value={recipe.name}
                                            onChange={(e) => setRecipe((c) => ({ ...c, name: e.target.value }))}
                                        />
                                    </div>
                                    <div className="rc-field">
                                        <label className="rc-field-label" htmlFor="rc-subject">One row is</label>
                                        {usableSubjects.length === 1 && !saved ? (
                                            <div className="rc-plain-value" id="rc-subject">
                                                {usableSubjects[0].label}
                                            </div>
                                        ) : (
                                            <select
                                                id="rc-subject"
                                                className="rc-select"
                                                value={recipe.subject || ''}
                                                onChange={(e) => changeSubject(e.target.value)}
                                            >
                                                {subjects.map((option) => (
                                                    <option
                                                        key={option.key}
                                                        value={option.key}
                                                        disabled={Boolean(option.locked_reason)}
                                                    >
                                                        {option.locked_reason
                                                            ? `${option.label} — ${option.locked_reason}`
                                                            : option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="rc-builder-actions">
                                        <button
                                            type="button"
                                            className="rc-btn-primary"
                                            onClick={handleSave}
                                            disabled={Boolean(busy)}
                                        >
                                            <FontAwesomeIcon icon={busy === 'save' ? faSpinner : faPlus} spin={busy === 'save'} />
                                            {busy === 'save' ? 'Saving…' : 'Save'}
                                        </button>
                                        {saved && !saved.is_locked && (
                                            <button
                                                type="button"
                                                className="rc-btn-ghost"
                                                onClick={() => runOnSaved('share', () => setReportShared(saved.id, !saved.is_shared))}
                                                disabled={Boolean(busy)}
                                            >
                                                <FontAwesomeIcon icon={faShareNodes} />
                                                {saved.is_shared ? 'Stop sharing' : 'Share'}
                                            </button>
                                        )}
                                        {saved && canLock && (
                                            <button
                                                type="button"
                                                className="rc-btn-ghost"
                                                onClick={() => runOnSaved('lock', () => setReportLocked(saved.id, !saved.is_locked))}
                                                disabled={Boolean(busy)}
                                            >
                                                <FontAwesomeIcon icon={faStar} />
                                                {saved.is_locked ? 'Remove standard' : 'Make standard'}
                                            </button>
                                        )}
                                        {saved && (
                                            <button
                                                type="button"
                                                className="rc-btn-danger-ghost"
                                                onClick={handleDelete}
                                                disabled={Boolean(busy)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} /> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {formError && (
                                    <div className="rc-error rc-error-spaced">
                                        <FontAwesomeIcon icon={faTriangleExclamation} /> {formError}
                                    </div>
                                )}

                                {/* Columns */}
                                <div className="rc-section">
                                    <div className="rc-section-title">Columns, in order</div>
                                    <SortableContext
                                        items={recipe.columns.map((c) => c.key)}
                                        strategy={rectSortingStrategy}
                                    >
                                        <div className="rc-column-strip">
                                            {recipe.columns.map((column) => (
                                                <ColumnChip
                                                    key={column.key}
                                                    column={column}
                                                    label={labelFor(column.key)}
                                                    editing={editingKey === column.key}
                                                    onStartEdit={() => setEditingKey(column.key)}
                                                    onCommitEdit={(value) => {
                                                        setRecipe((current) => renameColumn(current, column.key, value));
                                                        setEditingKey('');
                                                    }}
                                                    onCancelEdit={() => setEditingKey('')}
                                                    onRemove={() => setRecipe((current) => removeColumn(current, column.key))}
                                                />
                                            ))}
                                            <ColumnDropTarget />
                                        </div>
                                    </SortableContext>
                                </div>

                                {/* Filters */}
                                <div className="rc-section">
                                    <div className="rc-section-title">Filters</div>
                                    {recipe.filters.map((row, index) => {
                                        const point = pointsByKey[row.key];
                                        return (
                                            <div key={index} className="rc-rule-row">
                                                <select
                                                    className="rc-select"
                                                    value={row.key || ''}
                                                    aria-label="Data point to filter on"
                                                    onChange={(e) => {
                                                        const next = pointsByKey[e.target.value];
                                                        setFilter(index, {
                                                            key: e.target.value,
                                                            op: next?.ops?.[0]?.op || '',
                                                            value: '',
                                                        });
                                                    }}
                                                >
                                                    <option value="">Choose a data point…</option>
                                                    {(subject?.groups || []).map((group) => (
                                                        <optgroup key={group.name} label={group.name}>
                                                            {(group.data_points || []).map((p) => (
                                                                <option key={p.key} value={p.key}>{p.label}</option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                                <select
                                                    className="rc-select"
                                                    value={row.op || ''}
                                                    aria-label="Comparison"
                                                    disabled={!point}
                                                    onChange={(e) => setFilter(index, {
                                                        op: e.target.value,
                                                        value: e.target.value === 'between' ? ['', ''] : '',
                                                    })}
                                                >
                                                    {(point?.ops || []).map((op) => (
                                                        <option key={op.op} value={op.op}>{op.label}</option>
                                                    ))}
                                                </select>
                                                <FilterValue
                                                    point={point}
                                                    row={row}
                                                    onChange={(value) => setFilter(index, { value })}
                                                />
                                                <button
                                                    type="button"
                                                    className="rc-chip-icon-btn"
                                                    aria-label="Remove this filter"
                                                    onClick={() => setRecipe((current) => ({
                                                        ...current,
                                                        filters: current.filters.filter((_, i) => i !== index),
                                                    }))}
                                                >
                                                    <FontAwesomeIcon icon={faXmark} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                    <button
                                        type="button"
                                        className="rc-add-row-btn"
                                        onClick={() => setRecipe((current) => ({
                                            ...current,
                                            filters: [...current.filters, { key: '', op: '', value: '' }],
                                        }))}
                                    >
                                        <FontAwesomeIcon icon={faPlus} /> Add filter
                                    </button>
                                </div>

                                {/* Sort */}
                                <div className="rc-section">
                                    <div className="rc-section-title">Sort</div>
                                    {recipe.sort.map((rule, index) => (
                                        <div key={index} className="rc-rule-row">
                                            <select
                                                className="rc-select"
                                                value={rule.key || ''}
                                                aria-label="Data point to sort by"
                                                onChange={(e) => setSort(index, { key: e.target.value })}
                                            >
                                                <option value="">Choose a data point…</option>
                                                {(subject?.groups || []).map((group) => (
                                                    <optgroup key={group.name} label={group.name}>
                                                        {(group.data_points || []).map((p) => (
                                                            <option key={p.key} value={p.key}>{p.label}</option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </select>
                                            <select
                                                className="rc-select"
                                                value={rule.dir || 'asc'}
                                                aria-label="Sort direction"
                                                onChange={(e) => setSort(index, { dir: e.target.value })}
                                            >
                                                <option value="asc">A to Z</option>
                                                <option value="desc">Z to A</option>
                                            </select>
                                            <button
                                                type="button"
                                                className="rc-chip-icon-btn"
                                                aria-label="Remove this sort rule"
                                                onClick={() => setRecipe((current) => ({
                                                    ...current,
                                                    sort: current.sort.filter((_, i) => i !== index),
                                                }))}
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                        </div>
                                    ))}
                                    {recipe.sort.length < MAX_SORT_RULES && (
                                        <button
                                            type="button"
                                            className="rc-add-row-btn"
                                            onClick={() => setRecipe((current) => ({
                                                ...current,
                                                sort: [...current.sort, { key: '', dir: 'asc' }],
                                            }))}
                                        >
                                            <FontAwesomeIcon icon={faPlus} /> Add sort rule
                                        </button>
                                    )}
                                </div>

                                {/* Totals */}
                                <div className="rc-section">
                                    <div className="rc-section-title">Totals</div>
                                    {(() => {
                                        const totalable = recipe.columns.filter((c) => pointsByKey[c.key]?.totalable);
                                        if (totalable.length === 0) {
                                            return (
                                                <div className="rc-note">
                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                    Add a column holding an amount or a quantity and it can be totalled here.
                                                </div>
                                            );
                                        }
                                        return (
                                            <div className="rc-total-chips">
                                                {totalable.map((column) => (
                                                    <button
                                                        key={column.key}
                                                        type="button"
                                                        className={`rc-toggle-chip ${recipe.totals.includes(column.key) ? 'rc-toggle-chip-on' : ''}`}
                                                        aria-pressed={recipe.totals.includes(column.key)}
                                                        onClick={() => toggleTotal(column.key)}
                                                    >
                                                        {column.header || labelFor(column.key)}
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Live preview */}
                            <div className="rc-panel">
                                <div className="rc-section-title">Preview</div>
                                {previewError && (
                                    <div className="rc-error rc-error-spaced">
                                        <FontAwesomeIcon icon={faTriangleExclamation} /> {previewError}
                                    </div>
                                )}
                                {!previewError && (
                                    <ResultTable
                                        result={preview}
                                        running={previewing}
                                        runningText="Working out the preview…"
                                        previewRows={PREVIEW_ROWS}
                                        caption={`Preview of ${recipe.name || 'this report'}`}
                                        idleText="Add a column and the preview appears here."
                                        emptyText="No rows match this report yet. Widen a filter."
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </DndContext>
            )}
        </div>
    );
};

export default ReportBuilder;
